// Main App component with routing as specified in appmark.txt
// SWRM-115: Microsoft SSO OAuth callback route
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuthStore, useUIStore, useNotificationStore } from "./store";
import { Layout } from "./components/Layout";
import { NotificationContainer } from "./components/NotificationContainer";
import { Login } from "./pages/Login";
import { AuthCallback } from "./pages/AuthCallback";
import { Dashboard } from "./pages/Dashboard";
import { Agents } from "./pages/Agents";
import { Health } from "./pages/Health";
import { Settings } from "./pages/Settings";
import { WorkflowDetail } from "./pages/WorkflowDetail";
import { PipelineBuilder } from "./pages/PipelineBuilder";
import { PipelineList } from "./pages/PipelineList";
import { A2AAgents } from "./pages/A2AAgents";
import { Playbooks } from "./pages/Playbooks";
import { PlaybookDetail } from "./pages/PlaybookDetail";
import APIKeys from "./pages/APIKeys";
import { Groups } from "./pages/Groups";
import { Roles } from "./pages/Roles";
import { Users } from "./pages/Users";
import Approvals from "./components/Approval/Approvals";
import ApprovalDetailPage from "./components/Approval/ApprovalDetailPage";
import PRMonitoring from "./pages/PRMonitoring";
import { Permission, hasPermission } from "./utils/permissions";

// Placeholder components for other routes
const Workflows = () => <div className="p-6">Workflows page - Coming soon</div>;

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PermissionRoute: React.FC<{
  children: React.ReactNode;
  permission: string;
}> = ({ children, permission }) => {
  const user = useAuthStore((state) => state.user);

  if (!hasPermission(user, permission)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const theme = useUIStore((state) => state.theme);
  const addNotification = useNotificationStore((state) => state.addNotification);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Listen for RBAC 403 events and show toast (ADR-017)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      addNotification(detail || "Permission denied", "error");
    };
    window.addEventListener("rbac-denied", handler);
    return () => window.removeEventListener("rbac-denied", handler);
  }, [addNotification]);

  return (
    <Router>
      <div className="App">
        <NotificationContainer />
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* OAuth callback route (SWRM-115) - public, no redirect */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="workflows" element={<Workflows />} />
            <Route path="workflows/:id" element={<WorkflowDetail />} />
            <Route path="agents" element={<Agents />} />
            <Route path="pipelines" element={<PipelineList />} />
            <Route path="pipelines/builder" element={<PipelineBuilder />} />
            <Route path="pipelines/builder/:id" element={<PipelineBuilder />} />
            <Route path="playbooks" element={<Playbooks />} />
            <Route path="playbooks/:id" element={<PlaybookDetail />} />
            <Route path="a2a-agents" element={<A2AAgents />} />
            <Route path="api-keys" element={<PermissionRoute permission={Permission.API_KEY_CREATE}><APIKeys /></PermissionRoute>} />
            <Route path="groups" element={<PermissionRoute permission={Permission.GROUP_CREATE}><Groups /></PermissionRoute>} />
            <Route path="roles" element={<PermissionRoute permission={Permission.ROLE_CREATE}><Roles /></PermissionRoute>} />
            <Route path="users" element={<PermissionRoute permission={Permission.USER_ASSIGN_ROLE}><Users /></PermissionRoute>} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="approvals/:id" element={<ApprovalDetailPage />} />
            <Route path="pr-monitoring" element={<PRMonitoring />} />
            <Route path="health" element={<PermissionRoute permission={Permission.ADMIN}><Health /></PermissionRoute>} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
